/**
 * Common Gear API utility functions
 */

const getJsonData = (url) => fetchJsonData(url, 'GET');
const putJsonData = (url, body) => fetchJsonData(url, 'PUT', body);
const getTextData = url => fetchTextData(url, 'GET');

/**
 * Executes a JSON fetch against the given url, using the given HTTP method.
 * Any errors or unexpected responses will be thrown as a rejected Promise.
 *
 * @param url The API url
 * @param method The HTTP request method
 * @param body (optional) The body to include in the request
 * @return The JSON result
 */
const fetchJsonData = (url, method, body) => {
	return fetch(url, {
		mode: 'cors',
		credentials: 'include',
		method: method,
		timeout: 1000,
		headers: {
			'Accept': 'application/json',
			'Content-Type': 'application/json'
		},
		...body && { 'body': body }
	}).catch(e => {
		return Promise.reject(new Error("The network is currently unavailable. Please call us at 800-522-4762."));
	}).then(response => {
		if (response.status === 204) {
			return null;
		}
		if (response.ok) {
			return response.json();
		}
		return Promise.reject({
			message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
			response: response
		});
	});
};

/**
 * Executes a plain text fetch against the given url, using the given HTTP method.
 * Any errors or unexpected responses will be thrown as a rejected Promise.
 *
 * @param url The API url
 * @param method The HTTP request method
 * @param body (optional) The body to include in the request
 * @return The text result
 */
const fetchTextData = (url, method, body) => {
	return fetch(url, {
		mode: 'cors',
		credentials: 'include',
		method: method,
		timeout: 1000,
		headers: {
			Accept: 'text/plain',
			'Content-Type': 'text/plain'
		},
		...(body && { body: body })
	})
		.catch(e => {
			return Promise.reject(new Error('The network is currently unavailable. Please call us at 800-522-4762.'));
		})
		.then(response => {
			if (response.ok) {
				return response.text();
			}
			return Promise.reject({
				message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
				response: response
				}
			);
		});
};

/**
 * Fetch the oms requisition structures for a list of gear requisitions.
 *
 * @param gearRequisitionIds An array of Gear requisition Ids for which to fetch oms reqs.
 */
export const fetchOmsRequisitions = (gearRequisitionIds) => {
	return getJsonData(process.env.REACT_APP_GEAR_REST_URL + "/requisitions?gearReqIds=" + gearRequisitionIds.join(","));
};

/**
 * Fetches the oms requisition structure for the orderable item.
 *
 * @param orderableItemId The orderable item ID to fetch by
 * @param gsdlNo The logged in practitioner ID. Used for authorization checks
 * @return On success, the resolved Promise with the oms requisition. On failure or error, a rejected Promise with the error message.
 */
export const fetchOmsRequisitionByOrderableItemId = ( orderableItemId, gsdlNo ) => {
	return getJsonData(process.env.REACT_APP_GEAR_REST_URL + '/requisition/getReqByOrderableItem?orderableItemId=' + orderableItemId + '&gsdlNo=' + gsdlNo);
}

/**
 * Fetches the legacy packing slip for the given orderForm
 * 
 * The gearOrderId is passed in to be used for authorization checks.
 *
 * @param {Number} gearOrderId The id of the Gear order
 * @param {Number} orderFormId The order form id for which to fetch a packing slip
 */
export const fetchPackingSlip = (gearOrderId, orderFormId) => {
	return getTextData(process.env.REACT_APP_GEAR_REST_URL + `/order/${gearOrderId}/packingslip/${orderFormId}`);
}

export const fetchPackingSlipNo = (gearOrderId) => {
	return getTextData(process.env.REACT_APP_GEAR_REST_URL + '/packing-slip/fullPackingNo/order/' + gearOrderId);
}

/**
 * Fetches the legacy packing slip for the given orderForm
 *
 * The gearOrderId is passed in to be used for authorization checks.
 *
 * @param {Number} gearOrderId The id of the Gear order
 * @param {Number} orderFormId The order form id for which to fetch a packing slip
 */
export const fetchFullPackingNo = (orderFormId) => {
	return getTextData(process.env.REACT_APP_GEAR_REST_URL + `/order/fullPackingNo/${orderFormId}`);
}

/**
 * Submit an updated GearOrder to have the patient info and shipping method saved.
 *
 * @param {GearOrder} order The GearOrder containing patient info to save
 */
export const putPatientInfo = (order) => {
	return putJsonData(process.env.REACT_APP_GEAR_REST_URL + '/order/' + order.id + '/patient/', JSON.stringify(order));
}

/**
 * Mark the order as handed off and send an email to the patient.
 *
 * @param {Number} orderId The ID of the Gear order being handed off
 * @param {GearPatient} patientInfoInfo The patient's info (email address and zip code)
 * @param {String} type Indicates what type of email to send, regardless of the order details 
 */
export const handoffToPatient = (orderId, patientInfo, type) => {
	let url = `/order/${orderId}/handoff`;
	if (type) {
		url = `${url}/${type}`;
	}

	return putJsonData(process.env.REACT_APP_GEAR_REST_URL + url, JSON.stringify(patientInfo));
}

/**
 * 
 * @param {Number} orderId The ID of the Gear order to update
 * @param {Number} stepId The ID of the step to skip
 * @param {Number} [reqId] The Gear Req ID associated to the step being skipped
 */
export const skipStep = (orderId, stepId, reqId) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/order/${orderId}/skip-step/${stepId}`;
	if (reqId) {
		url = url + '/' + reqId;
	}
	return putJsonData(url);
};

/**
 * Gets the userInfo details from the access-token
 */
export const getAccountType = () => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/api/getAccountType`);
};

export const getPatientAcknowledgement = (gearOrderId) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/acknowledgement?gearOrderId=${gearOrderId}`)
}

/**
 * Gets the list of previously saved patient instructions for this practitioner.
 * Instructions for the given orderableItemId will be first.
 * 
 * @param gsdlNo The order's practitioner ID
 * @param orderableItemId The ID of the Orderable Item to prioritize
 * @return The list of previously saved patient instructions, or an empty list if none are found
 */
export const getPreviousPatientInstructions = (gsdlNo, orderableItemId) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/practitioner/${gsdlNo}/previousInstructions/${orderableItemId}`);
}

/**
 * Gets the list of active STAR issues for the signed in practitioner.
 * The list will include issues for any practitioner the signed in user has access "on behalf of".
 */
export const getActiveStars = () => {
	// Use the original window.fetch when calling gdx_web_url so we don't create an uneccessary nonce entry
	return window
		._fetch(`${process.env.REACT_APP_GDX_WEB_URL}/mygdx/account/json/active-stars`, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
		}).catch(e => {
			return Promise.reject(new Error("The network is currently unavailable. Please call us at 800-522-4762."));
		}).then(response => {
			if (response.ok) {
				return response.json();
			}
			return Promise.reject({
				message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
				response: response
			});
		});
}

/**
 * Gets the list of practitioners the currently signed-in myGDX user has access "on behalf of" (Including themselves).
 * The list is filtered by OMS Requisiiton practitioners if a gearOrderId is provided.
 * 
 * @return An array of practitioner options
 */
export const fetchPractitionerOptions = (gearOrderId) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/practitioner-options`;
	if (gearOrderId) {
		url += '/' + gearOrderId;
	}
	return getJsonData(url);
}

/**
 * Gets the given gear order using the gear id as a param
 *
 * @param gearOrderId the gear order id
 * @param dob
 * @param zipCode
 * @param recaptcha
 * @return An GearOrder object
 */
export const validatePatientForGearOrder = (gearOrderId, dob, zipCode, recaptcha) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/validation/${gearOrderId}?dateOfBirth=${dob}&zipCode=${zipCode}&recaptcha=${recaptcha}`);
}

export const fetchGearOrder = (gearOrderId, dob) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}?dateOfBirth=${dob}`);
}

/**
 * Gets the Practitioner details by gsdlNo.
 * 
 * @return The practitioner details, or null if not found
 */
export const fetchPractitionerByGsdlNo = (gsdlNo) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/practitioner/${gsdlNo}`);
}

/**
 * Gets the list of previous insurances for any matching patients
 * 
 * @return An array of Insurance details for the patient
 */
export const fetchInsuranceListByPatientDetails = (gsdlNo, nameLast, dob, nameFirst) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/api/insurance-lookup?gsdlNo=${gsdlNo}&nameLast=${nameLast}&dob=${dob}`;
	if (nameFirst) {
		url = url + `&nameFirst=${nameFirst}`;
	}
	return getJsonData(url);
}

/**
 * Fetch the list of Patient Management records for the practitioner.
 * If the practitioner is empty, all patients the signed-in user has
 *  access to will be listed (delegates/office accounts)
 *
 * @param gsdlNo The practitioner ID for which to fetch records
 */
export const fetchPatientManagementList = (gsdlNo) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/patient-management/list`;
	if (gsdlNo) {
		url += `?gsdlNo=${gsdlNo}`;
	}
	return getJsonData(url);
}

/**
 * Upload a CSV file containing patient data and returns a Promise that resolves with the
 * newly created PatientManagement records
 * 
 * @param file The file to upload
 * @param gsdlNo The practitioner ID to associate to the records
 * @return The list of PatientManagement records parsed from the uploaded file
 */
export const uploadPatientCsvData = (file, gsdlNo) => {
	const formData = new FormData();
	formData.append('file', file);
	formData.append('gsdlNo', gsdlNo);

	// We can't use fetchJsonData because this call doesn't use json content-type
	return fetch(`${process.env.REACT_APP_GEAR_REST_URL}/patient-management/uploadCSV`, {
		mode: 'cors',
		credentials: 'include',
		method: 'post',
		timeout: 1000,
		headers: {
			Accept: 'application/json'
		},
		body: formData
	})
		.then((response) => {
			if (response.ok) {
				return response.json();
			}
			return Promise.reject({
				message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
				response: response
			});
		});
}

/**
 * Cancel a gear order using a packingNo
 *
 * @param gearOrderId the order id for the cancellation
 * @param dateOfBirth a date of birth identification for the user
 * @return
 */
export const cancelGearOrder = (gearOrderId, dateOfBirth) => {
	// We can't use fetchJsonData because this call doesn't use json content-type
	return fetch(`${process.env.REACT_APP_GEAR_REST_URL}/order/cancel/${gearOrderId}?dateOfBirth=${dateOfBirth}`, {
		mode: 'cors',
		credentials: 'include',
		method: 'delete',
		timeout: 1000,
		headers: {
			Accept: 'application/json'
		}
	})
	.then((response) => {
		if (response.ok) {
			return response.text();
		}
		return Promise.reject({
			message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
			response: response
		});
	});
}

/**
 * Cancel a Gear Requisition.
 */
export const cancelGearReq = (gearOrderId, gearReqId, dateOfBirth) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/requisition/${gearReqId}/cancel?dateOfBirth=${dateOfBirth}`);
}

/**
 * Send a list of PatientManagement IDs to the server to be deleted
 * 
 * @param recordsToDelete A map of PatientManagement records to delete. The map's key is the practitioner's gsdlNo.
 *                        The map's values are the lists of IDs to delete.
 * @return true if the delete was successful. A rejected Promise if an error occurred.
 */
export const deletePatientManagementRecords = (recordsToDelete) => {
	return fetch(`${process.env.REACT_APP_GEAR_REST_URL}/patient-management/delete`, {
		mode: 'cors',
		credentials: 'include',
		method: 'delete',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		},
		body: JSON.stringify(recordsToDelete)
	}).then((response) => {
		if (response.ok) {
			return true;
		}
		return Promise.reject({
			message: 'An unexpected error has occurred. Please call us at 800-522-4762.',
			response: response
		});
	});
};

/**
 * Submit the payment info, if needed, and confirm the order.
 *
 * @param {Number} orderId TheID of the GearOrder for which to submit payment and confirmation
 * @param {GearOrderPayment} orderPayment The payment details to submit.
 * @return The updated Gear Order as JSON
 */
export const submitOrderConfirmationAndPayment = (orderId, orderPayment) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${orderId}/confirm-and-pay`, JSON.stringify(orderPayment));
}

/**
 * Gets the available shipping charge amounts for the order.
 * 
 * @return The list of available shipping charges
 */
export const fetchAvailableShippingChargesByOrder = (gearOrderId) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/available-shipping-charges`);
}

/**
 * Submit Medical Necessity Acknowledgement date.
 * 
 * @param orderId The ID of the GearOrder to update
 * @param acknowledgementDate The Date that Medical Necessity was acknowleged (may be null if unacknowledged)
 */
export const updateMedicalNecessityAcknowledgement = (orderId, acknowledgementDate) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${orderId}/update-medical-necessity`, JSON.stringify(acknowledgementDate));
}

/**
 * Update a Gear Requisition with new Patient Instructions and Clinical Findings.
 * 
 * @param dropShipInstructions The updated details
 * @return A Promise that resolves with the updated GearOrder
 */
export const updateDropShipInstructions = (dropShipInstructions) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/dropShipInstructions`, JSON.stringify(dropShipInstructions));
}

/**
 * Save Sample Collection details
 * 
 * @param gearRequisition The Gear Requisition object with the sample collection details to be saved
 * @return A Promise that resolves with the full Gear Order
 */
export const saveSampleCollection = (gearRequisition) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/sampleCollection`, JSON.stringify(gearRequisition));
}

/**
 * Initiate a re-collection for the given order number
 * 
 * @param accessionNo The order to re-collect
 * @return A Promise that resolves with the recollection response
 */
export const recollect = (accessionNo) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/re-collect/${accessionNo}`);
}

/**
 * Move the GearReq off of this order and save it for future registration
 * 
 * @param gearOrderId The ID of the order containing the GearReq
 * @param gearReqId The ID of the GearReq to move
 * @param queryString (Optional) query string containing authentication values like dateOfBirth
 */
export const saveForLater = (gearOrderId, gearReqId, queryString) => {
	const url = `${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/requisition/${gearReqId}/save-for-later` + (queryString || '');
	return putJsonData(url);
}

/**
 * Fetch the details of a discount code
 * 
 * @param gearOrderId The ID of the order the discount will be applied to
 * @param discountCode The code to fetch
 */
export const getDiscountCode = (gearOrderId, discountCode) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/api/${gearOrderId}/applyDiscount/${encodeURIComponent(discountCode)}`);
}

/**
 * Fetches order history
 */
export const getAdvancedOrderSearchResults = (searchForm) => {
	return putJsonData(process.env.REACT_APP_GEAR_REST_URL + '/advanced-order-search', JSON.stringify(searchForm));
}

/**
 * Prepare an order for recollection by clearing out certain data and marking the order as not confirmed
 * 
 * @param gearOrderId the ID of the order to be recollected
 * @param gearReqIds An array of Gear Req Ids to be recollected
 * @param dateOfBirth That patient's date of birth
 */
export const requestRecollection = (gearOrderId, dateOfBirth, gearReqIds) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/request-recollect?dateOfBirth=${dateOfBirth}&gearReqIds=${gearReqIds?.join(',')}`);
}

/**
 * Prepare an order for reorder by clearing out certain data and resubmitting to kitordering
 *
 * @param gearOrderId the ID of the order to be reordered
 * @param reorderGearReqs An array of Gear Req objects to be reordered
 */
export const requestReorder = (gearOrderId, reorderGearReqs) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/request-reorder`, JSON.stringify(reorderGearReqs));
}

/**
 * Save Practitioner authorization for the order
 * 
 * @param orderId The ID of the GearOrder to update
 */
export const authorizeOrder = (orderId) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/orders/${orderId}/authorize`);
}

/**
 * Updates the practitioner on the order.
 * 
 * @param orderId The ID of the GearOrder to upate
 * @param gsdlNo The ID of the new practitioner
 */
export const changeOrderPractitioner = (orderId, gsdlNo) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${orderId}/change-practitioner/${gsdlNo}`);
}

/**
 * Logs the confirmed price increase and emails the patient.
 */
export const confirmPriceIncrease = (gearOrderId, gearReqId, updateSource) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${gearOrderId}/confirm-price-increase/${gearReqId}/${updateSource}`);
}

/**
 * Fetches the list of recommended products appropriate for the Gear order.
 * 
 * @param gearOrderId The ID of the Gear Order for which to fetch recommended products
 * @param maxProductsToReturn (optional) The maximum number of recommended products to retrieve
 */
export const fetchRecommendedProductsForOrder = (gearOrderId, maxProductsToReturn) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/recommended-products/order/${gearOrderId}`;
	if (Number.isInteger(maxProductsToReturn)) {
		url += `?limit=${maxProductsToReturn}`
	}
	return getJsonData(url);
}

/**
 * Submit a log event to be saved
 */
export const logEvent = (formattedMessage, event, arg1, arg2, arg3, logLevel) => {
	let url = `${process.env.REACT_APP_GEAR_REST_URL}/log`;
	if (logLevel) {
		url += `/${logLevel}`;
	}

	const loggingEvent = {
		formattedMessage,
		arg0: event,
		arg1,
		arg2,
		arg3
	}

	putJsonData(url, JSON.stringify(loggingEvent));
}

export const saveTestSelections = (gearOrder, queryString) => {
	let url = process.env.REACT_APP_GEAR_REST_URL + '/testSelection';
	if (queryString) {
		url += queryString;
	}

	return putJsonData(url, JSON.stringify(gearOrder));
}

/**
 * Fetch the invoice balance due for the order.
 */
export const getInvoiceBalanceDue = (orderNo) => {
	const url = `${process.env.REACT_APP_GDX_WEB_URL}/amount-due?invoiceNo=${orderNo}`;

	// Use the original window.fetch when calling gdx_web_url so we don't create an uneccessary nonce entry
	return window
		._fetch(url, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.catch((e) => {
			return Promise.reject();
		})
		.then((response) => {
			if (response.ok) {
				// The request must be application/json but the response will be a raw number (or empty).
				// Read as text to handle the possible empty response.
				return response.text();
			}
			return Promise.reject({ response: response });
		});
};

/**
 * Create a new order from the saved tests on a bundle
 */
export const orderFromBundle = (bundleOrderRequest) => {
	return putJsonData(
		`${process.env.REACT_APP_GEAR_REST_URL}/bundle/${bundleOrderRequest.gearBundleId}/order`,
		JSON.stringify(bundleOrderRequest)
	);
}

export const saveBundle = (gearBundle) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/bundle/save`, JSON.stringify(gearBundle));
}

export const fetchBundle = (gearBundleId) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/bundle/${gearBundleId}`);
}

export const deleteBundle = (gearBundleId) => {
	return fetchJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/bundle/${gearBundleId}`, 'DELETE');
}

/*
 * Fetches the lists of items the practitioner can order.
 */
export const fetchPractitionerOrderableItems = (gsdlNo) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/practitioner/${gsdlNo}/orderableItems`);
}

/**
 * Fetches PTPAY and IIP input field pricing for the OmsOrderableItem and Practitioner
 */
export const fetchInputFieldPriceMap = (omsOrderableItemId, gsdlNo) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/api/inputFieldPriceMap/${omsOrderableItemId}?gsdlNo=${gsdlNo}`);
}

export const createPayPalOrder = (gearOrderId, purchaseUnit) => {
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/paypal/order/${gearOrderId}`, JSON.stringify(purchaseUnit));
}

export const transferPitlOrder = (pitlTransferRequest, dob) => {
	logEvent(
		'{} - PITL Transfer submitted. gearOrderId={}, gearReqIds={}',
		'UI_ACTION_PITL_TRANSFER_SUBMIT',
		pitlTransferRequest.gearOrderId,
		pitlTransferRequest.gearReqIds.join(', ')
	);
	return putJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/${pitlTransferRequest.gearOrderId}/pitl-transfer?dateOfBirth=${dob}`, JSON.stringify(pitlTransferRequest));

}

export const getNSAAcknowledgement = (gearOrderId) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/nsa-acknowledgement?gearOrderId=${gearOrderId}`)
}

export const getNewOrderForPractLookup = (gsdlNo, captchaValue) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/new-for-searched-pract/${gsdlNo}?recaptcha=${captchaValue || ''}`);
}

export const getShippingChargesByCountry = (country) => {
	return getJsonData(`${process.env.REACT_APP_GEAR_REST_URL}/order/available-shipping-charges/${country}`);
};